import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { DeliveryAddressInformation, RegisterDeliveryAddressRequest, UpdateDeliveryAddressRequest } from "../../../../../model/delivery-address";
import { useRegisterDeliveryAddressMutation, useUpdateDeliveryAddressInfoMutation } from "../../../../../service/delivery-address-service";
import { useLazyGetStatesByCountryQuery } from "../../../../../service/state-service";
import { Input } from "../../../components/input";
import { Select, SelectOption } from "../../../components/select";
import { PhoneInput } from "../../../components/phone-input";
import styles from "./billing-information-form.module.scss";
import { RootState } from "../../../../../stores/store";
import { useSelector } from "react-redux";
import { validatePhoneNumber } from "../../../validators";
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout";
import { CustomerLoader } from "../../../components";

type Props = {
  onCancel: () => void;
  onSaved: () => void;
  addressToEdit?: UpdateDeliveryAddressRequest;
};

export const BillingInformationForm: React.FC<Props> = ({ onCancel, onSaved, addressToEdit }) => {
  const countryCode = useSelector((state: RootState) => state.user.countryCode);
  const isEdit = !!addressToEdit;

  const [stateOptions, setStateOptions] = useState<SelectOption[]>([]);
  const [getStates, { isLoading, isFetching }] = useLazyGetStatesByCountryQuery();
  const [createAddress, { isLoading: isCreating }] = useRegisterDeliveryAddressMutation();
  const [updateAddress, { isLoading: isUpdating }] = useUpdateDeliveryAddressInfoMutation();

  const isSaving = isCreating || isUpdating;

  const { control, handleSubmit, formState: { errors } } = useForm<DeliveryAddressInformation>({
    mode: "onSubmit",
    defaultValues: {
      region: addressToEdit?.deliveryAddressInformation?.region ?? "",
      city: addressToEdit?.deliveryAddressInformation?.city ?? "",
      address: addressToEdit?.deliveryAddressInformation?.address ?? "",
      fullName: addressToEdit?.deliveryAddressInformation?.fullName ?? "",
      email: addressToEdit?.deliveryAddressInformation?.email ?? "",
      phoneNumber: addressToEdit?.deliveryAddressInformation?.phoneNumber ?? "",
    },
  });

  const fetchStates = useCallback(async () => {
    try {
      const states = await getStates(countryCode).unwrap();
      setStateOptions(states.map((s) => ({ value: s.name, label: s.name })));
    } catch {
      toast.error("Failed to load states");
    }
  }, [getStates, countryCode]);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  const onSubmit = async (data: DeliveryAddressInformation) => {
    const addressInfo = {
      fullName: data.fullName,
      phoneNumber: data.phoneNumber,
      address: data.address,
      region: data.region,
      city: data.city,
      email: data.email,
      countryISOCode: countryCode,
    };

    try {
      if (isEdit) {
        const updateRequest: UpdateDeliveryAddressRequest = {
          deliveryAddressId: addressToEdit!.deliveryAddressId,
          deliveryAddressInformation: addressInfo,
        };
        await updateAddress(updateRequest).unwrap();
        toast.success("Address updated successfully");
      } else {
        const createRequest: RegisterDeliveryAddressRequest = {
          deliveryAddressInformation: addressInfo,
        }
        await createAddress(createRequest).unwrap();
        toast.success("Address created successfully");
      }
      onSaved();
    } catch (error: any) {
      toast.error(error?.data?.title || "Failed to save address. Please try again.");
    }
  };

  if (isLoading || isFetching) {
    return (
      <CustomerPageLayout>
        <CustomerLoader
          title={"Loading billing address form..."}
          subtitle="Please wait while we fetch all necessary information..." />
      </CustomerPageLayout>
    )
  };

  return (
    <div className={styles.wrapper}>
      <div>
        <span className={styles.title}>
          {isEdit ? "Edit Address & Contact" : "Add Address & Contact"}
        </span>
        <p className={styles.subtitle}>Manage your billing information</p>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Customer Address</h3>

        <Controller
          name="region"
          control={control}
          rules={{ required: "State/Region is required" }}
          render={({ field }) => (
            <Select
              options={stateOptions}
              placeholder="State/Region"
              value={field.value}
              onChange={field.onChange}
              error={errors.region?.message}
              variant="solid"
              label="State/Region"
            />
          )}
        />

        <Controller
          name="city"
          control={control}
          rules={{
            required: "City/District is required",
            pattern: {
              value: /^[^\d]+$/,
              message: "City/District cannot contain numbers",
            },
          }}
          render={({ field }) => (
            <Input
              placeholder="City/District"
              value={field.value}
              onChange={field.onChange}
              error={errors.city?.message}
              variant="solid"
            />
          )}
        />

        <Controller
          name="address"
          control={control}
          rules={{ required: "Address is required" }}
          render={({ field }) => (
            <Input
              placeholder="Address"
              value={field.value}
              onChange={field.onChange}
              error={errors.address?.message}
              variant="solid"
            />
          )}
        />
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Contact Info</h3>

        <Controller
          name="fullName"
          control={control}
          rules={{
            required: "Contact full name is required",
            pattern: {
              value: /^[^\d]+$/,
              message: "Contact full name cannot contain numbers",
            },
          }}
          render={({ field }) => (
            <Input
              placeholder="Contact Full Name"
              value={field.value}
              onChange={field.onChange}
              error={errors.fullName?.message}
              variant="solid"
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          rules={{
            required: "Email address is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address",
            },
          }}
          render={({ field }) => (
            <Input
              type="email"
              placeholder="Email Address"
              value={field.value}
              onChange={field.onChange}
              error={errors.email?.message}
              variant="solid"
            />
          )}
        />

        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            required: "Phone number is required",
            validate: (value) => {
              return validatePhoneNumber(value);
            }
          }}
          render={({ field, fieldState }) => (
            <PhoneInput
              value={field.value}
              onChange={(phone) => field.onChange(phone)}
              error={fieldState.error?.message}
              placeholder="Phone Number"
              className={styles.phoneInput}
            />
          )}
        />
      </div>

      <div className={styles.actionBar}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.saveButton}
          onClick={handleSubmit(onSubmit)}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};
